import { CalendarDate, DateValue } from '@internationalized/date'
import { Button, Card, CardBody, CardHeader, DateRangePicker, RangeValue } from '@nextui-org/react'
import {
  getLastActiveUsersCSV,
  getSignupsCSV,
  getUserActivitiesCSV,
  getUserTaskSubmission,
  getRecentInactiveUsers,
  getTestDriveEmails,
  getTestDriveUsersTaskSubmissionCount,
  getInactiveUsers
} from '~/services/superAdminService'
import {
  convertDateValueToDate,
  convertToUTC,
  downloadBlobAsFile,
  getDefaultDateRange,
  PreviewData
} from '~/pages/super-admin'
import { useEffect, useRef, useState } from 'react'
import { FaDownload } from '@react-icons/all-files/fa/FaDownload'
import { FaEye } from '@react-icons/all-files/fa/FaEye'

interface CsvFile {
  name: string
  description: string
  filename: string
  downloadFn: (startDate: Date, endDate: Date) => Promise<Blob>
}

interface dateRangeSelectionObject {
  [key: string]: RangeValue<DateValue>
}

const csvFiles: CsvFile[] = [
  {
    name: 'New Created Users',
    description: 'Export of all user signups within the selected date range',
    filename: 'user-signups.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getSignupsCSV(params.toString())
    }
  },
  {
    name: 'Last Active Users',
    description: 'Export of users who have been active within the selected date range',
    filename: 'last-active-users.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getLastActiveUsersCSV(params.toString())
    }
  },
  {
    name: 'User Activity Log',
    description:
      'Export of user task completion metrics and activity counts for the selected period',
    filename: 'user-activities.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getUserActivitiesCSV(params.toString())
    }
  },
  {
    name: 'User Task Submission Log',
    description: 'Export of user task submission logs within the selected date range',
    filename: 'user-task-submissions.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getUserTaskSubmission(params.toString())
    }
  },
  {
    name: 'User Recent Inactivity Log',
    description: "Export of users who haven't submitted any tasks within the selected date range",
    filename: 'user-inactivity.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getRecentInactiveUsers(params.toString())
    }
  },
  {
    name: 'User Inactivity Log',
    description:
      "Export of users who haven't submitted any tasks since they signed up, date range ignored",
    filename: 'user-inactivity-log.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      return await getInactiveUsers(params.toString())
    }
  },
  {
    name: 'Test Drive Email Log',
    description: 'Export of all test drive email logs within the selected date range',
    filename: 'test-drive-emails.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getTestDriveEmails(params.toString())
    }
  },
  {
    name: 'Test Drive User Task Submission Log',
    description: 'Export of user task submission counts within the selected date range',
    filename: 'test-drive-user-submission-count.csv',
    downloadFn: async (startDate: Date, endDate: Date) => {
      const params = new URLSearchParams()
      params.append('startDate', convertToUTC(startDate))
      params.append('endDate', convertToUTC(endDate, true))
      return await getTestDriveUsersTaskSubmissionCount(params.toString())
    }
  }
]

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<CsvFile | null>(null)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const dateRangeSelectionsRef = useRef<dateRangeSelectionObject>({})

  useEffect(() => {
    csvFiles.forEach(file => {
      dateRangeSelectionsRef.current[file.filename] = getDefaultDateRange()
    })
  }, [])

  const handleDownload = async (file: CsvFile) => {
    try {
      setIsLoading(true)
      const dateRange = dateRangeSelectionsRef.current[file.filename]
      const startTime = convertDateValueToDate(dateRange.start)
      const endTime = convertDateValueToDate(dateRange.end)
      const blob = await file.downloadFn(startTime, endTime)
      downloadBlobAsFile(blob, file.filename)
    } catch (error) {
      console.error('Error downloading file:', error)
      alert('Failed to download file')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePreview = async (file: CsvFile) => {
    setSelectedFile(file)
    setIsLoading(true)
    try {
      const dateRange = dateRangeSelectionsRef.current[file.filename]
      const startTime = convertDateValueToDate(dateRange.start)
      const endTime = convertDateValueToDate(dateRange.end)
      const blob = await file.downloadFn(startTime, endTime)
      const text = await blob.text()
      const rows = text.split('\n').map(row => row.split(','))
      const headers = rows[0]
      const dataRows = rows.slice(1, 101)

      setPreviewData({ headers, rows: dataRows })
    } catch (error) {
      console.error('Error fetching preview:', error)
      setPreviewData(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDateRangeChange = (range: RangeValue<DateValue>, filename: string) => {
    if (!range) return
    const dateRange = (dateRangeSelectionsRef.current[filename] = range)
    setPreviewData(null)
    setSelectedFile(null)
  }

  const isDateRangeDisabled = (name: string) => {
    return ['User Inactivity Log'].includes(name)
  }

  return (
    <>
      <div className='mb-4'>
        <h1 className='text-2xl font-bold'>Super Admin Dashboard</h1>
        <p className='text-gray-600'>Preview and download system data</p>
      </div>

      <div className='grid gap-3 mb-4'>
        {csvFiles.map(file => (
          <Card key={file.filename} radius='sm' shadow='sm'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 p-4'>
              <div>
                <h2 className='text-lg font-semibold'>{file.name}</h2>
                <p className='text-sm text-gray-600'>{file.description}</p>
              </div>
              <div className='flex gap-2 items-center'>
                <div className='flex items-center gap-2'>
                  {/* Conditionally render date selection UI */}
                  <div className='flex items-center gap-2'>
                    <label className='text-sm text-gray-600 whitespace-nowrap'>Date Range</label>
                    <DateRangePicker
                      defaultValue={getDefaultDateRange() as any}
                      onChange={range => handleDateRangeChange(range, file.filename)}
                      isDisabled={isDateRangeDisabled(file.name)}
                    />
                  </div>
                </div>
                <Button
                  size='sm'
                  variant='flat'
                  onClick={() => handlePreview(file)}
                  isLoading={isLoading && selectedFile?.filename === file.filename}
                  className='flex items-center gap-2 min-w-24'
                >
                  {isLoading && selectedFile?.filename === file.filename ? null : (
                    <FaEye className='h-4 w-4' />
                  )}
                  Preview
                </Button>
                <Button
                  size='sm'
                  color='primary'
                  onClick={() => handleDownload(file)}
                  isLoading={isLoading && selectedFile?.filename === file.filename}
                  className='flex items-center gap-2 min-w-24'
                >
                  {isLoading && selectedFile?.filename === file.filename ? null : (
                    <FaDownload className='h-4 w-4' />
                  )}
                  Download
                </Button>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {selectedFile && (
        <Card className='mt-4' radius='sm' shadow='sm'>
          <CardHeader className='p-4 flex flex-row justify-between gap-x-2'>
            <h2 className='text-lg font-semibold'>Preview: {selectedFile.name}</h2>
            <p className='text-sm text-gray-600'>Showing first 100 rows</p>
          </CardHeader>
          <CardBody className='p-4'>
            {isLoading ? (
              <div className='text-center py-4 select-none'>Loading preview...</div>
            ) : previewData ? (
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse'>
                  <thead>
                    <tr className='bg-gray-50'>
                      {previewData.headers.map((header, i) => (
                        <th key={i} className='border p-2 text-left font-medium'>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row, i) => (
                      <tr key={i} className='border-b hover:bg-gray-50'>
                        {row.map((cell, j) => (
                          <td key={j} className='border p-2'>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='text-center py-4 text-gray-500'>No preview available</div>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}

export default Dashboard
